import type {
  AgentInstallJob,
  AgentListResponse,
  AgentLlmTest,
  AgentServerTest,
  AgentSetup,
  AgentSkill,
  AgentSkillsResponse,
  UpdateAgentSetupBody,
  User,
} from '@tasks/contracts';

import { apiRequest, apiRequestVoid } from './client';

/** AI-агенты: список и установка (docs/16 §7). */

export function listAgents(signal?: AbortSignal): Promise<AgentListResponse> {
  return apiRequest<AgentListResponse>('/admin/agents', { signal });
}

/** Общий AI-агент: singleton на роль (docs/17 §11, docs/18 §5.1). */
export function createSharedAgent(body: {
  agentRole: 'analyst' | 'developer';
  displayName?: string;
}): Promise<User> {
  return apiRequest<User>('/admin/agents/shared', { method: 'POST', body });
}

/** Control-токен общего агента: секрет показывается один раз. */
export function createAgentControlToken(id: string): Promise<{ token: string }> {
  return apiRequest<{ token: string }>(
    `/admin/agents/${encodeURIComponent(id)}/control-token`,
    { method: 'POST' },
  );
}

export function getAgentSetup(id: string, signal?: AbortSignal): Promise<AgentSetup> {
  return apiRequest<AgentSetup>(`/admin/agents/${encodeURIComponent(id)}/setup`, { signal });
}

/** Удаление агента из Tasks: настройки и навыки стираются, задачи остаются. */
export function deleteAgent(id: string): Promise<void> {
  return apiRequestVoid(`/admin/agents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function updateAgentSetup(id: string, body: UpdateAgentSetupBody): Promise<AgentSetup> {
  return apiRequest<AgentSetup>(`/admin/agents/${encodeURIComponent(id)}/setup`, {
    method: 'PATCH',
    body,
  });
}

export function testAgentServer(id: string): Promise<AgentServerTest> {
  return apiRequest<AgentServerTest>(`/admin/agents/${encodeURIComponent(id)}/server/test`, {
    method: 'POST',
  });
}

export function testAgentLlm(id: string): Promise<AgentLlmTest> {
  return apiRequest<AgentLlmTest>(`/admin/agents/${encodeURIComponent(id)}/llm/test`, {
    method: 'POST',
  });
}

/** Установка общего агента: projectId не нужен, проекты приходят привязками. */
export function startSharedAgentInstall(id: string): Promise<{ job: AgentInstallJob }> {
  return apiRequest<{ job: AgentInstallJob }>(`/admin/agents/${encodeURIComponent(id)}/install`, {
    method: 'POST',
    body: {},
  });
}

export function getAgentInstall(id: string, signal?: AbortSignal): Promise<{ job: AgentInstallJob }> {
  return apiRequest<{ job: AgentInstallJob }>(`/admin/agents/${encodeURIComponent(id)}/install`, {
    signal,
  });
}

export function cancelAgentInstall(id: string, jobId: string): Promise<void> {
  return apiRequestVoid(
    `/admin/agents/${encodeURIComponent(id)}/install/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  );
}

export function updateAgent(id: string): Promise<{ job: AgentInstallJob }> {
  return apiRequest<{ job: AgentInstallJob }>(`/admin/agents/${encodeURIComponent(id)}/update`, {
    method: 'POST',
    body: {},
  });
}

export function applyAgent(id: string): Promise<{ job: AgentInstallJob }> {
  return apiRequest<{ job: AgentInstallJob }>(`/admin/agents/${encodeURIComponent(id)}/apply`, {
    method: 'POST',
  });
}

export function uninstallAgent(
  id: string,
  body: { confirmName: string; deleteData: boolean },
): Promise<{ job: AgentInstallJob }> {
  return apiRequest<{ job: AgentInstallJob }>(`/admin/agents/${encodeURIComponent(id)}/uninstall`, {
    method: 'POST',
    body,
  });
}

export function agentInstallLogUrl(id: string, jobId: string): string {
  return `/api/admin/agents/${encodeURIComponent(id)}/install/${encodeURIComponent(jobId)}/log`;
}

/** Свои навыки агента (docs/16 §4.9). */
export function listAgentSkills(id: string, signal?: AbortSignal): Promise<AgentSkillsResponse> {
  return apiRequest<AgentSkillsResponse>(`/admin/agents/${encodeURIComponent(id)}/skills`, {
    signal,
  });
}

export function uploadAgentSkill(
  id: string,
  filename: string,
  content: string,
): Promise<AgentSkill> {
  return apiRequest<AgentSkill>(
    `/admin/agents/${encodeURIComponent(id)}/skills/${encodeURIComponent(filename)}`,
    { method: 'PUT', body: { content } },
  );
}

export function deleteAgentSkill(id: string, filename: string): Promise<void> {
  return apiRequestVoid(
    `/admin/agents/${encodeURIComponent(id)}/skills/${encodeURIComponent(filename)}`,
    { method: 'DELETE' },
  );
}
